import {
  defaultAccountOfKind,
  defaultVoucherDate,
  entryAccounts,
  splitPostingAccount,
} from './reference-data.js';
import type { VoucherTypeCode, VoucherTypeConfig } from './voucher-config.js';
import {
  emptyLine,
  summarise,
  toPayload,
  validate,
  type Currency,
  type FxContext,
  type Role,
  type Validation,
  type VoucherDraft,
  type VoucherPayload,
  type VoucherSummary,
} from './voucher-model.js';
import { fromExcelSerial, looksLikeExcelSerial, readXlsx } from './xlsx.js';

/**
 * Bulk voucher upload: CSV or xlsx in, the same postings the form makes out.
 *
 * The file is flat — one row per **line**, with a `Voucher ref` column grouping
 * rows into documents. That is the shape an accountant already has, because it
 * is the shape a bank statement, a payroll run and an old system's export all
 * come in. Reshaping it into nested JSON is this module's job, not theirs.
 *
 * **The rules are not re-implemented here.** A parsed group is turned into a
 * `VoucherDraft` and handed to `summarise` and `validate` — the very functions
 * the entry screen calls — so a bulk-uploaded Bank Payment generates its contra
 * line the same way, rounds its base amounts the same way and is refused for the
 * same reasons as one typed by hand. A second implementation of the posting
 * would be a second set of bugs, and the one that only runs on import is the one
 * nobody notices is wrong.
 */

// ── Columns ──────────────────────────────────────────────────────────────────

/**
 * Header aliases, in the order they are offered in the template.
 *
 * Several names per column because the file usually comes from somewhere else:
 * "Dr" and "Debit" are the same column, and rejecting a file over the spelling
 * of a header helps nobody.
 */
const COLUMNS = {
  reference: ['voucher ref', 'voucher', 'ref', 'reference', 'voucher no', 'group'],
  date: ['date', 'voucher date', 'txn date'],
  contraAccount: [
    'cash/bank account',
    'cash account',
    'bank account',
    'contra account',
    'paid from',
    'received into',
  ],
  party: ['party', 'party code', 'customer', 'supplier'],
  narration: ['narration', 'voucher narration', 'description', 'particulars'],
  account: ['account', 'account code', 'gl account', 'code'],
  amount: ['amount', 'payment', 'receipt', 'value'],
  debit: ['debit', 'dr'],
  credit: ['credit', 'cr'],
  lineNarration: ['line narration', 'line description', 'remarks', 'detail'],
  currency: ['currency', 'ccy'],
  fxRate: ['fx rate', 'rate', 'exchange rate'],
} as const;

type ColumnKey = keyof typeof COLUMNS;

function normaliseHeader(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, ' ')
    .replace(/\.$/, '');
}

/** Header row → column key → index. Unknown headers are ignored, not rejected. */
function mapHeaders(header: string[]): Partial<Record<ColumnKey, number>> {
  const map: Partial<Record<ColumnKey, number>> = {};
  header.forEach((raw, index) => {
    const name = normaliseHeader(raw);
    for (const [key, aliases] of Object.entries(COLUMNS) as Array<[ColumnKey, readonly string[]]>) {
      if (map[key] === undefined && aliases.includes(name)) map[key] = index;
    }
  });
  return map;
}

// ── CSV ──────────────────────────────────────────────────────────────────────

/**
 * RFC 4180, including the parts people forget.
 *
 * A narration containing a comma is the common case, not the edge case, so
 * quoted fields have to work; `""` inside a quoted field is a literal quote; and
 * a quoted field may contain newlines, which is why this is a character loop and
 * not `split('\n').map(line => line.split(','))`.
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;

  // A BOM is what Excel writes when it saves UTF-8, and it would otherwise
  // become part of the first header's name.
  const input = text.replace(/^﻿/, '');

  for (let i = 0; i < input.length; i++) {
    const character = input[i];

    if (quoted) {
      if (character === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        field += character;
      }
      continue;
    }

    if (character === '"') {
      quoted = true;
    } else if (character === ',' || character === ';' || character === '\t') {
      row.push(field);
      field = '';
    } else if (character === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (character !== '\r') {
      field += character;
    }
  }

  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows.map((r) => r.map((cell) => cell.trim()));
}

/** Reads whichever of the two formats the file is, by extension then by magic. */
export async function readSpreadsheet(file: File): Promise<string[][]> {
  const isXlsx =
    file.name.toLowerCase().endsWith('.xlsx') ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  if (isXlsx) return readXlsx(file);

  // A .xls saved from a modern Excel is often really a .xlsx; a real one is
  // OLE2 and cannot be read here. Say which it is rather than failing on XML.
  const head = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  if (head[0] === 0xd0 && head[1] === 0xcf) {
    throw new Error(
      'That is a legacy .xls file. Open it in Excel and save as .xlsx or CSV, then upload again.',
    );
  }
  if (head[0] === 0x50 && head[1] === 0x4b) return readXlsx(file);

  return parseCsv(await file.text());
}

// ── Parse ────────────────────────────────────────────────────────────────────

export interface BulkIssue {
  /** 1-based row in the uploaded file, header included, so it matches Excel. */
  row: number;
  message: string;
}

export interface ParsedVoucher {
  reference: string;
  /** Where this voucher starts in the file, for "row 14" in the preview. */
  row: number;
  draft: VoucherDraft;
  fx: FxContext;
  summary: VoucherSummary;
  validation: Validation;
  payload: VoucherPayload;
}

export interface BulkParse {
  vouchers: ParsedVoucher[];
  /** Problems that stopped a row being read at all — not validation failures. */
  issues: BulkIssue[];
}

export interface BulkDefaults {
  currency: Currency;
  fxRate: string;
  /** The book's reporting currency, so a row naming it gets rate 1. */
  baseCurrency: Currency;
  branchCode: string | null;
  role: Role;
}

interface RawLine {
  row: number;
  account: string;
  debit: string;
  credit: string;
  narration: string;
  /** Per line, not per document — a receipt can settle two customers at once. */
  party: string;
}

interface Group {
  reference: string;
  row: number;
  date: string;
  narration: string;
  contraAccount: string;
  currency: Currency;
  fxRate: string;
  lines: RawLine[];
}

/** Amounts arrive with grouping separators and currency symbols. Strip both. */
function cleanAmount(raw: string): string {
  return raw.replace(/[\s,]/g, '').replace(/^(PKR|USD|Rs\.?|\$)/i, '');
}

/**
 * `30/07/2026`, `2026-07-30` and Excel's `46236` all mean the same day.
 *
 * Day-first is assumed for the slashed form, because this is a Pakistani book
 * and `dd/mm/yyyy` is what the accountants there write. An ISO date is passed
 * through untouched, which is what the template asks for and what removes the
 * ambiguity entirely.
 */
function normaliseDate(raw: string): string {
  const value = raw.trim();
  if (value === '') return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (looksLikeExcelSerial(value)) return fromExcelSerial(Number(value));

  const slashed = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(value);
  if (slashed) {
    const [, day, month, year] = slashed as unknown as [string, string, string, string];
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return value;
}

/**
 * Turn a grid into vouchers.
 *
 * Rows are grouped by `Voucher ref`, in the order they first appear, and the
 * document-level fields — date, party, narration, cash/bank account, currency —
 * are taken from the group's first row. A later row that disagrees is not an
 * error: it is a spreadsheet where somebody filled the date down the column, and
 * treating that as a conflict would reject the most common file there is.
 */
export function parseRows(
  rows: string[][],
  type: VoucherTypeCode,
  config: VoucherTypeConfig,
  defaults: BulkDefaults,
): BulkParse {
  const issues: BulkIssue[] = [];

  const header = rows.find((row) => row.some((cell) => cell !== ''));
  if (!header) return { vouchers: [], issues: [{ row: 1, message: 'The file is empty.' }] };

  const headerRow = rows.indexOf(header);
  const columns = mapHeaders(header);

  if (columns.account === undefined) {
    return {
      vouchers: [],
      issues: [
        {
          row: headerRow + 1,
          message:
            'No Account column found. Download the template for this voucher type — the header row has to name the columns.',
        },
      ],
    };
  }

  const hasAmountColumn =
    columns.amount !== undefined || columns.debit !== undefined || columns.credit !== undefined;
  if (!hasAmountColumn) {
    return {
      vouchers: [],
      issues: [
        {
          row: headerRow + 1,
          message:
            config.shape === 'JOURNAL'
              ? 'No Debit or Credit column found.'
              : `No ${config.amountLabel} column found.`,
        },
      ],
    };
  }

  const at = (row: string[], key: ColumnKey): string => {
    const index = columns[key];
    return index === undefined ? '' : (row[index] ?? '').trim();
  };

  const groups = new Map<string, Group>();

  for (let i = headerRow + 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.every((cell) => cell === '')) continue;
    const fileRow = i + 1;

    const account = at(row, 'account');
    const amount = cleanAmount(at(row, 'amount'));
    const debit = cleanAmount(at(row, 'debit'));
    const credit = cleanAmount(at(row, 'credit'));

    if (account === '' && amount === '' && debit === '' && credit === '') continue;

    // Without a ref column every row is its own voucher, which is exactly right
    // for a file of one-line payments and harmless for anything else.
    const reference = at(row, 'reference') || `Row ${fileRow}`;

    let group = groups.get(reference);
    if (!group) {
      // Any ISO-shaped code is taken at face value and the *database* decides
      // whether the book knows it: `currency_code` is an enum a super
      // administrator extends, so a hard-coded pair here would reject a
      // currency that posts perfectly well. A malformed cell falls back to the
      // session currency rather than failing the row, which is the same
      // forgiveness every other column in this importer shows.
      const currencyRaw = at(row, 'currency').toUpperCase();
      const currency: Currency = /^[A-Z]{3}$/.test(currencyRaw) ? currencyRaw : defaults.currency;
      const rate = cleanAmount(at(row, 'fxRate'));

      group = {
        reference,
        row: fileRow,
        date: normaliseDate(at(row, 'date')) || defaultVoucherDate(),
        narration: at(row, 'narration'),
        contraAccount: at(row, 'contraAccount'),
        currency,
        fxRate: currency === defaults.baseCurrency ? '1' : rate || defaults.fxRate,
        lines: [],
      };
      groups.set(reference, group);
    }

    if (account === '') {
      issues.push({ row: fileRow, message: 'Row has an amount but no account — skipped.' });
      continue;
    }

    // A CONTRA voucher has one amount column and a side fixed by the type; a JV
    // has two and the accountant chooses. Reading both shapes from the same
    // parser means a file with Debit/Credit still imports as a Cash Payment.
    let lineDebit = debit;
    let lineCredit = credit;
    if (config.shape !== 'JOURNAL' && amount !== '') {
      if (config.analysisSide === 'Dr') lineDebit = amount;
      else lineCredit = amount;
    }

    if (lineDebit === '' && lineCredit === '') {
      issues.push({ row: fileRow, message: `${account} has no amount — skipped.` });
      continue;
    }

    group.lines.push({
      row: fileRow,
      account,
      debit: lineDebit,
      credit: lineCredit,
      narration: at(row, 'lineNarration'),
      // Falls back to the first row's party so a one-party file needs the column
      // filled once, the way a spreadsheet is usually written.
      party: at(row, 'party') || group.lines[0]?.party || '',
    });
  }

  const vouchers: ParsedVoucher[] = [];

  for (const group of groups.values()) {
    if (group.lines.length === 0) continue;

    const draft: VoucherDraft = {
      voucherNo: null,
      voucherDate: group.date,
      narration: group.narration,
      // The column is optional now that each voucher type has a fixed cash or
      // bank account: a file that names one is honoured, a file that leaves the
      // column out posts to the same account the entry screen would.
      contraAccountCode:
        group.contraAccount || (config.contra ? defaultAccountOfKind(config.contra.kind) : ''),
      lines: group.lines.map((line) => ({
        ...emptyLine(),
        // A spreadsheet names one account per row — the leaf being posted to,
        // which for sub-ledger detail is a 5th-level code. The grid edits a head
        // and a sub-account, so split it the same way a loaded voucher is split;
        // otherwise an imported row would show a blank Account column and its
        // sub-account would look unset.
        ...splitPostingAccount(line.account),
        narration: line.narration,
        debit: line.debit,
        credit: line.credit,
        partyCode: line.party,
      })),
      status: 'NEW',
      approvalState: 'NOT_REQUIRED',
    };

    const fx: FxContext = {
      currency: group.currency,
      fxRate: group.fxRate,
      baseCurrency: defaults.baseCurrency,
    };
    const summary = summarise(draft, config, fx);
    const validation = validate(draft, config, summary, defaults.role, fx);

    vouchers.push({
      reference: group.reference,
      row: group.row,
      draft,
      fx,
      summary,
      validation,
      payload: toPayload(type, draft, summary, fx, defaults.branchCode),
    });
  }

  return { vouchers, issues };
}

// ── Template ─────────────────────────────────────────────────────────────────

/**
 * A CSV the accountant can open, fill in and upload back.
 *
 * CSV rather than xlsx because a template is a header row and two examples, and
 * generating a valid xlsx to carry that would mean writing a ZIP. Excel opens
 * this, edits it and — if they prefer — saves it back as .xlsx, which the
 * importer reads either way.
 */
export function templateCsv(type: VoucherTypeCode, config: VoucherTypeConfig): string {
  const amountHeader = config.shape === 'JOURNAL' ? ['Debit', 'Credit'] : [config.amountLabel];

  const header = [
    'Voucher ref',
    'Date',
    ...(config.contra ? ['Cash/Bank account'] : []),
    'Party',
    'Narration',
    'Account',
    ...amountHeader,
    'Line narration',
    'Currency',
    'FX rate',
  ];

  const today = defaultVoucherDate();
  // The two example codes come out of the chart rather than being written here.
  // They were `'6310'` and `'2130'`, which 0037 renumbered — and a downloaded
  // template whose sample rows name accounts the book does not carry teaches the
  // wrong shape before anybody uploads anything. First expense, first liability,
  // in chart order; blank on a book whose chart has not loaded, which reads as a
  // column to fill rather than a code to trust.
  // A leaf that takes a manual JV: `entryAccounts()` also offers 4th-level heads
  // as the way to reach their sub-ledger detail, and a control head in a sample
  // row is a row `trg_voucher_lines_derive` refuses.
  const sample = (type: 'EXPENSE' | 'LIABILITY') =>
    entryAccounts().find((a) => a.type === type && a.isLeaf && a.allowDirectPosting)?.code ?? '';
  const examples: string[][] =
    config.shape === 'JOURNAL'
      ? [
          [
            'V1',
            today,
            '',
            'Month-end accrual',
            sample('EXPENSE'),
            '25000.00',
            '',
            'Freight accrual',
            'PKR',
            '1',
          ],
          [
            'V1',
            today,
            '',
            'Month-end accrual',
            sample('LIABILITY'),
            '',
            '25000.00',
            'Accrued expenses',
            'PKR',
            '1',
          ],
        ]
      : [
          [
            'V1',
            today,
            '',
            '',
            config.narrationPlaceholder,
            '',
            '15000.00',
            'First line',
            'PKR',
            '1',
          ],
        ];

  // The contra column sits third, so the example rows need a placeholder there
  // to stay aligned with the header.
  const rows = config.contra
    ? examples
    : examples.map((row) => [...row.slice(0, 2), ...row.slice(3)]);

  const escape = (cell: string) =>
    /[",;\t\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;

  const lines = [header, ...rows].map((row) => row.map(escape).join(','));

  return [
    ...lines,
    '',
    `# ${config.label} (${type}) — one row per line, grouped by Voucher ref.`,
    '# Date accepts 2026-07-30 or 30/07/2026. Amounts are plain numbers; commas are ignored.',
    ...(config.contra
      ? [
          `# Cash/Bank account is the ${config.contra.label.toLowerCase()} account — same on every row of a voucher.`,
        ]
      : []),
    '# Currency and FX rate are optional; blank uses the top bar. Delete these comment lines before uploading.',
  ].join('\r\n');
}

/** Hands the browser a file. Same mechanism the report exports will use. */
export function downloadText(
  filename: string,
  text: string,
  mime = 'text/csv;charset=utf-8',
): void {
  // The BOM is what makes Excel open a UTF-8 CSV as UTF-8 rather than as the
  // system codepage, which is the difference between "Meezan" and "Meezan".
  const blob = new Blob([`﻿${text}`], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
